import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  LockIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import type { DocumentStatus } from "~/lib/api";
import { cn } from "~/lib/cn";

/**
 * BLOC Design System (BDS) UI キット（docs/design_handoff_admin_redesign/ DesignTokens の部品見本）。
 * プレゼンテーション専用・data 非依存の小部品。色は global.css のトークンを
 * Tailwind v4 の `[var(--x)]` 形で参照する（裸の `[--x]` は v4 で無効）。
 */

/* ── ステータスバッジ（Document status 3 値） ──────────────────── */
const STATUS_LABEL: Record<DocumentStatus, string> = {
  published: "公開中",
  pending_changes: "更新あり",
  draft: "下書き",
};
const STATUS_CLS: Record<DocumentStatus, string> = {
  published:
    "text-[var(--st-pub-fg)] bg-[var(--st-pub-bg)] border-[var(--st-pub-border)]",
  pending_changes:
    "text-[var(--st-pend-fg)] bg-[var(--st-pend-bg)] border-[var(--st-pend-border)]",
  draft:
    "text-[var(--st-draft-fg)] bg-[var(--st-draft-bg)] border-[var(--st-draft-border)]",
};

export function StatusBadge({
  status,
  className,
}: {
  status: DocumentStatus;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-[11.5px] font-medium whitespace-nowrap",
        STATUS_CLS[status],
        className,
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/* ── ステータスドット（稼働 / ジョブ状態） ─────────────────────── */
export type DotStatus = "completed" | "running" | "failed" | "idle";
const DOT_CLS: Record<DotStatus, string> = {
  completed: "bg-[var(--ok)]",
  running: "bg-[var(--warn)]",
  failed: "bg-destructive",
  idle: "bg-[var(--faint)]",
};

export function StatusDot({
  status,
  label,
  className,
}: {
  status: DotStatus;
  label?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-[12.5px] whitespace-nowrap",
        className,
      )}
    >
      <span className={cn("size-[7px] rounded-full", DOT_CLS[status])} />
      {label}
    </span>
  );
}

/* ── ロールバッジ（PlatformAdmin / Org Admin / Org Editor） ──────── */
export type BlocRole = "platformAdmin" | "orgAdmin" | "orgEditor";
const ROLE_LABEL: Record<BlocRole, string> = {
  platformAdmin: "BLOC管理者",
  orgAdmin: "組織管理者",
  orgEditor: "編集者",
};
// BDS は role 専用トークンを使わず primary / accent / muted を再配分する
// （DesignTokens 正本）: PA=濃紺塗り / OA=BH青ティント / OE=ニュートラル枠付き。
const ROLE_CLS: Record<BlocRole, string> = {
  platformAdmin: "bg-primary text-primary-foreground font-bold",
  orgAdmin: "bg-accent text-accent-foreground font-bold",
  orgEditor: "border border-border bg-muted text-muted-foreground font-semibold",
};

export function RoleBadge({
  role,
  className,
}: {
  role: BlocRole;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] tracking-[0.02em] whitespace-nowrap",
        ROLE_CLS[role],
        className,
      )}
    >
      {ROLE_LABEL[role]}
    </span>
  );
}

/* ── ロックバッジ（ロック / 権限） ─────────────────────────────── */
export function LockBadge({
  label = "ロック",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-[5px] border border-border bg-muted px-1.5 py-px text-[10.5px] font-semibold whitespace-nowrap text-[var(--lock)]",
        className,
      )}
    >
      <LockIcon className="size-3" />
      {label}
    </span>
  );
}

/* ── 代理操作バッジ（impersonation / acting） ───────────────────── */
export function ActingBadge({
  label = "BLOC管理者として操作中",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[26px] items-center gap-1.5 rounded-md border border-[var(--acting-bd)] bg-[var(--acting-bg)] px-2.5 text-[11.5px] font-medium whitespace-nowrap text-[var(--acting-fg)]",
        className,
      )}
    >
      <span className="size-1.5 rounded-full bg-[var(--faint)]" />
      {label}
    </span>
  );
}

/* ── アバター（組織=角丸スクエア / 人物=円形） ──────────────────── */
export function Avatar({
  name,
  kind = "person",
  size = 32,
  dot,
  className,
}: {
  name: string;
  kind?: "org" | "person";
  size?: number;
  dot?: DotStatus;
  className?: string;
}) {
  const initial = name?.trim()?.[0] ?? "?";
  const radius = kind === "org" ? Math.round(size * 0.27) : size; // org=角丸 / person=円
  return (
    <span className={cn("relative inline-flex shrink-0", className)} style={{ width: size, height: size }}>
      <span
        className={cn(
          "flex w-full items-center justify-center font-bold",
          kind === "org"
            ? "bg-primary text-primary-foreground"
            : "bg-accent text-accent-foreground",
        )}
        style={{ borderRadius: radius, fontSize: Math.round(size * 0.42) }}
      >
        {initial}
      </span>
      {dot && (
        <span
          className={cn(
            "absolute -right-0.5 -bottom-0.5 rounded-full border-2 border-card",
            DOT_CLS[dot],
          )}
          style={{ width: Math.max(8, size * 0.28), height: Math.max(8, size * 0.28) }}
        />
      )}
    </span>
  );
}

/* ── バナー / アラート ─────────────────────────────────────────── */
export type BannerVariant = "info" | "warn" | "error" | "success";
const BANNER_CLS: Record<BannerVariant, string> = {
  info: "border-accent bg-accent text-accent-foreground",
  warn: "border-[var(--st-pend-border)] bg-[var(--st-pend-bg)] text-[var(--st-pend-fg)]",
  error: "border-[var(--st-red-border)] bg-[var(--st-red-bg)] text-[var(--st-red-fg)]",
  success: "border-[var(--st-pub-border)] bg-[var(--st-pub-bg)] text-[var(--st-pub-fg)]",
};
const BANNER_ICON: Record<BannerVariant, ComponentType<{ className?: string }>> = {
  info: InfoIcon,
  warn: TriangleAlertIcon,
  error: CircleAlertIcon,
  success: CircleCheckIcon,
};

export function Banner({
  variant = "info",
  title,
  children,
  actions,
  onClose,
  className,
}: {
  variant?: BannerVariant;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** 渡すと右端に閉じるボタンを描画（toast / 任意に閉じられるバナー用）。 */
  onClose?: () => void;
  className?: string;
}) {
  const Icon = BANNER_ICON[variant];
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-[9px] border px-3.5 py-3",
        BANNER_CLS[variant],
        className,
      )}
    >
      <Icon className="mt-px size-4 shrink-0" />
      <div className="min-w-0 flex-1 text-[12.5px] leading-relaxed">
        {title && <div className="font-semibold">{title}</div>}
        {children}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="-mt-0.5 -mr-1 shrink-0 rounded p-0.5 text-current/65 hover:bg-foreground/5 hover:text-current"
        >
          <XIcon className="size-3.5" />
        </button>
      )}
    </div>
  );
}
